// The stock ledger as a table. Nothing changes stock without a row here: every take, return,
// delivery, transfer, count, adjustment and FieldRoutes application is one line — who, when,
// product, quantity, from → to, reason, the visit it was for, and the receipt or photo.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { errorText, getLedger, qty } from "./useChemicalsApi";
import "./Chemicals.css";

const TYPE_WORDS = {
  receive: "Received", consume: "Used", consume_reversal: "Use reversed", transfer_in: "Moved in",
  transfer_out: "Moved out", adjust: "Adjusted up", adjust_out: "Adjusted down", return: "Returned to vendor",
  return_in: "Put back",
};
const when = (v) => new Date(v).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const EMPTY = { q: "", location: "", reason: "", type: "", since: "", until: "" };

export default function LedgerPage() {
  const [filters, setFilters] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const set = (k) => (e) => { setFilters((f) => ({ ...f, [k]: e.target.value })); setPage(1); };

  const load = useCallback(async () => {
    setErr("");
    try {
      const params = Object.fromEntries(Object.entries({ ...filters, page, page_size: 50 }).filter(([, v]) => v !== ""));
      setData(await getLedger(params));
    } catch (e) {
      setErr(errorText(e, "Couldn't load the ledger."));
    }
  }, [filters, page]);
  useEffect(() => { load(); }, [load]);

  const pages = useMemo(() => (data ? Math.max(1, Math.ceil(data.count / data.page_size)) : 1), [data]);

  return (
    <div className="chem-page">
      <section className="chem-card">
        <div className="chem-head">
          <div>
            <h2>Ledger</h2>
            <div className="chem-sub">Every movement is a row. On-hand anywhere is the sum of these rows — nothing changes stock without one.</div>
          </div>
          {data ? <div className="chem-stat"><div className="chem-stat__val">{data.count.toLocaleString()}</div><div className="chem-stat__label">movements</div></div> : null}
        </div>
        <form className="chem-form" onSubmit={(e) => { e.preventDefault(); load(); }}>
          <label>Search<input value={filters.q} onChange={set("q")} placeholder="product, note, location" /></label>
          <label>Location<select value={filters.location} onChange={set("location")}>
            <option value="">All</option>
            {(data?.locations || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select></label>
          <label>Reason<select value={filters.reason} onChange={set("reason")}>
            <option value="">All</option>
            {(data?.reasons || []).map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select></label>
          <label>Movement<select value={filters.type} onChange={set("type")}>
            <option value="">All</option>
            {(data?.types || []).map((t) => <option key={t} value={t}>{TYPE_WORDS[t] || t}</option>)}
          </select></label>
          <label>From<input type="date" value={filters.since} onChange={set("since")} /></label>
          <label>To<input type="date" value={filters.until} onChange={set("until")} /></label>
          <button type="button" className="chem-btn" onClick={() => { setFilters(EMPTY); setPage(1); }}>Clear</button>
        </form>
        {err ? <div className="chem-msg chem-msg--err">{err}</div> : null}
        {data === null ? <div className="chem-empty">Loading…</div> : data.results.length === 0 ? (
          <div className="chem-empty">No movements match.</div>
        ) : (
          <div className="chem-table-wrap">
            <table className="chem-table">
              <thead><tr>
                <th>When</th><th>Who</th><th>Movement</th><th>Product</th><th className="num">Qty</th>
                <th>From → to</th><th>Reason</th><th>Visit</th><th>Receipt / photo</th><th>Note</th>
              </tr></thead>
              <tbody>{data.results.map((r) => (
                <tr key={r.id}>
                  <td>{when(r.when)}</td>
                  <td>{r.by || <span className="chem-detail">system</span>}</td>
                  <td>{TYPE_WORDS[r.type] || r.type}</td>
                  <td><Link to={`../products/${r.product_id}/ledger`}>{r.product}</Link></td>
                  <td className={`num ${r.qty < 0 ? "chem-neg" : ""}`}>{r.qty > 0 ? "+" : ""}{qty(r.qty, r.uom)}</td>
                  <td>{[r.from, r.to].filter(Boolean).join(" → ") || r.location}</td>
                  <td>{(data.reasons.find((x) => x.key === r.reason) || {}).label || r.reason || "—"}</td>
                  <td>{r.appointment ? `${r.appointment.customer || "Appointment"}${r.appointment.start ? ` · ${when(r.appointment.start)}` : ""}`
                       : r.fieldroutes_appointment_id ? `FieldRoutes ${r.fieldroutes_appointment_id}` : "—"}</td>
                  <td>{r.photo?.url ? <a href={r.photo.url} target="_blank" rel="noreferrer">{r.photo.name || "open"}</a> : r.photo ? "attached" : "—"}</td>
                  <td className="chem-detail">{r.note}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
        {data && pages > 1 ? (
          <div className="chem-pills" style={{ marginTop: "var(--s-3)" }}>
            <button type="button" className="chem-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
            <span className="chem-detail">Page {page} of {pages}</span>
            <button type="button" className="chem-btn" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        ) : null}
      </section>
    </div>
  );
}
